/**
 * Lesson slot styling — web re-exports and dark-mode helpers.
 */
import type {
  AgeGroupCalendarColorLookup,
  CalendarColorMode,
  LessonSlotColorSource,
  LessonSlotStyleProps,
} from '@court-sync/shared';
import {
  appendSchedulePastOpacity,
  getLessonCalendarChipClasses as getSharedLessonCalendarChipClasses,
  getLessonCalendarChipStyleProps as getSharedLessonCalendarChipStyleProps,
  getLessonSlotClassesForLesson as getSharedLessonSlotClassesForLesson,
  getLessonSlotStyleProps as getSharedLessonSlotStyleProps,
  getScheduleOccurrenceSlotStyleProps as getSharedScheduleOccurrenceSlotStyleProps,
} from '@court-sync/shared';
import type { AcademySport } from '@court-sync/shared';
import { buildAcademySportLookup, type AcademySportLookupEntry } from './sportPresentation';

export {
  LESSON_SLOT_BALL_COLORS,
  type LessonSlotBallColor,
  CAMP_CALENDAR_CHIP_CLASSES,
  PRIVATE_PROGRAM_CALENDAR_CHIP_CLASSES,
  PRIVATE_PACKAGE_CALENDAR_CHIP_CLASSES,
  CANCELLED_CALENDAR_CHIP_CLASSES,
  POSTPONED_CALENDAR_CHIP_CLASSES,
  RESCHEDULED_CALENDAR_CHIP_CLASSES,
  SCHEDULE_PAST_OPACITY_CLASSES,
  PAST_SCHEDULE_SLOT_CLASSES,
  POSTPONED_SCHEDULE_SLOT_CLASSES,
  REPLACEMENT_SCHEDULE_SLOT_CLASSES,
  appendSchedulePastOpacity,
  isScheduleOccurrencePast,
  type AgeGroupCalendarColorLookup,
  type AgeGroupBallColorLookup,
  type LessonSlotColorSource,
  type LessonSlotStyleProps,
  type CalendarColorMode,
  resolveLessonCalendarBaseHex,
  resolveLessonSlotBallColor,
  resolveLessonCalendarColorTokens,
  getLessonSlotClasses,
  getLessonSlotClassesFromBallColor,
  buildAgeGroupCalendarColorLookup,
  buildAgeGroupBallColorLookup,
  getLessonSlotRnColors,
  LESSON_SLOT_RN_COLORS,
  DEFAULT_UNGROUPED_CALENDAR_COLOR,
} from '@court-sync/shared';

export type SportAccentLookup = Map<string, AcademySportLookupEntry>;

/** For filter pills / onboarding — not calendar paint. */
export function buildSportAccentLookup(sports: readonly AcademySport[]): SportAccentLookup {
  return buildAcademySportLookup(sports);
}

export function getCalendarColorMode(): CalendarColorMode {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

export function getLessonSlotClassesForLesson(
  lesson: LessonSlotColorSource,
  ageGroupById?: Record<string, AgeGroupCalendarColorLookup>,
  _sportLookup?: SportAccentLookup
): string {
  return getSharedLessonSlotClassesForLesson(lesson, ageGroupById, getCalendarColorMode());
}

export function getLessonSlotStylePropsForLesson(
  lesson: LessonSlotColorSource,
  ageGroupById?: Record<string, AgeGroupCalendarColorLookup>,
  options?: { isPast?: boolean; density?: 'soft' | 'solid' }
): LessonSlotStyleProps {
  return getSharedLessonSlotStyleProps(lesson, {
    ageGroupById,
    mode: getCalendarColorMode(),
    ...options,
  });
}

export interface LessonCalendarChipOptions {
  ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
  isPast?: boolean;
  overrideType?: 'cancelled' | 'postponed' | null;
  isRescheduledReplacement?: boolean;
  isStandalonePrivate?: boolean;
  isCamp?: boolean;
  /** @deprecated Sport no longer paints calendar chips */
  sportLookup?: SportAccentLookup;
}

export function getLessonCalendarChipClasses(
  lesson: LessonSlotColorSource,
  options?: LessonCalendarChipOptions
): string {
  return getSharedLessonCalendarChipClasses(lesson, {
    ageGroupById: options?.ageGroupById,
    isPast: options?.isPast,
    overrideType: options?.overrideType ?? undefined,
    isRescheduledReplacement: options?.isRescheduledReplacement,
    isStandalonePrivate: options?.isStandalonePrivate,
    isCamp: options?.isCamp,
    mode: getCalendarColorMode(),
  });
}

export function getLessonCalendarChipStyleProps(
  lesson: LessonSlotColorSource,
  options?: LessonCalendarChipOptions
): LessonSlotStyleProps {
  return getSharedLessonCalendarChipStyleProps(lesson, {
    ageGroupById: options?.ageGroupById,
    isPast: options?.isPast,
    overrideType: options?.overrideType ?? undefined,
    isRescheduledReplacement: options?.isRescheduledReplacement,
    isStandalonePrivate: options?.isStandalonePrivate,
    isCamp: options?.isCamp,
    mode: getCalendarColorMode(),
  });
}

export function getScheduleOccurrenceSlotClasses(
  lesson: LessonSlotColorSource,
  ageGroupById: Record<string, AgeGroupCalendarColorLookup> | undefined,
  _sportLookup: SportAccentLookup | undefined,
  isPast: boolean
): string {
  return appendSchedulePastOpacity(
    getLessonSlotClassesForLesson(lesson, ageGroupById),
    isPast
  );
}

export function getScheduleOccurrenceSlotStyleProps(
  lesson: LessonSlotColorSource,
  ageGroupById: Record<string, AgeGroupCalendarColorLookup> | undefined,
  isPast: boolean
): LessonSlotStyleProps {
  return getSharedScheduleOccurrenceSlotStyleProps(lesson, ageGroupById, isPast, getCalendarColorMode());
}
